import type { PromptResolve } from '@ur-solutions/react-components'
import { Icon, useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { useQuery } from '@apollo/client'
import type { ComboboxItem } from '@/components'
import { Combobox } from '@/components'
import type { ExtendedIconProps } from '@/components_deprecated'
import {
  Input as BaseInput,
  RegularModal,
  TextArea,
} from '@/components_deprecated'
import { Field, Form } from '@/modules/companies'
import type { ExportIssueForm } from '@/modules/issues/types'
import type { UserNode } from '@/modules/users'
import { USERS_COMBOBOX_QUERY } from '@/modules/users/queries'
import { getUserLabel } from '@/modules/users/util'
import { useUser } from '@/utils'
import { useMemo, useState } from 'react'
import styled from 'styled-components'

import { emailValidation, notEmptyArrayValidation } from '@/utils/forms'
import { ExportFormat, ExportOption, ExportType } from '@/modules/exports'
import {
  useExportFormatOptions,
  useExportOptions,
} from '@/modules/exports/hooks'

const Users = styled.div`
  border-radius: 5px;
  overflow: hidden;

  .userItem {
    padding: 0.6rem;
    border-bottom: 1px solid ${props => props.theme.colors.gray9};

    display: flex;
    align-items: center;
    .text {
      flex: 1;
    }
  }
  .userItem:last-child {
    border-bottom: none;
  }
  ${props => props.theme.layout.desktop} {
    max-height: 300px;
    overflow-y: auto;
  }
`

const Input = styled(BaseInput).attrs({
  background: 'white',
  height: '3.875rem',
})`
  margin-top: 0.6rem;
`

interface ExportIssuePromptProps {
  user: UserNode
  defaultText?: string | null
  onSubmit: PromptResolve<ExportIssueForm | null>
}

export const ExportIssuePrompt: React.FC<ExportIssuePromptProps> = ({
  // TODO unused?
  user: _user,
  defaultText,
  onSubmit,
}) => {
  const translations = useTranslate({
    exportIssue: 'issues.export-issue',
    export: 'common.export',
    selectedRecipients: 'issues.selected-recipients',
    selectFromList: 'issues.select-recipient-from-list',
    addWithEmail: 'issues.add-recipient-with-email',

    email: 'common.email',
    download: 'common.download',
    errorEmail: 'error.valid-email-required',

    type: 'common.type',
    exportOption: 'common.export-type',
    exportFormat: 'common.export-format',
    customText: 'issues.export-custom-text',

    toasts: {
      emailAlreadySelected: 'common.email-already-selected',
    },
    selectUsersError: 'common.select-at-least-one-recipient',
  })

  /**
   * * Info: The email field is handled seperately because a race condition
   * * when inside same form as the rest */
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const addToast = useToast()

  const {
    formValues: form,
    formErrors: errors,
    updateForm,
    submitHandler: submit,
  } = useForm<ExportIssueForm>({
    values: {
      exportOption: ExportOption.SAVE,
      recipients: [],
      exportFormat: ExportFormat.PDF,
      exportType: ExportType.ISSUES,
      customExportText: defaultText ?? '',
    },
    validators: {
      recipients: (value, form) =>
        notEmptyArrayValidation(value) &&
        form.exportOption === ExportOption.MAIL
          ? translations.selectUsersError
          : null,
    },
  })

  const exportOptions = useExportOptions()
  const exportFormatOptions = useExportFormatOptions([
    ExportFormat.TWENTYFOUR_SEVEN_OFFICE,
    ExportFormat.POWEROFFICE,
    ExportFormat.VISMA,
    ExportFormat.EXCEL,
    ExportFormat.EXCEL_FLAT,
    ExportFormat.TRIPLETEX,
    ExportFormat.PDF_SALARY,
  ])

  const me = useUser()

  const { data: usersData } = useQuery(USERS_COMBOBOX_QUERY, {
    variables: { includeSuperuser: me.isSuperuser },
  })

  const userItems = useMemo<ComboboxItem<string>[]>(
    () =>
      (usersData?.allUsersList ?? []).map(user => ({
        value: user.email,
        label: getUserLabel(user) || user.email,
        filterValue: user.internalNumber + ' ' + (user.fullName || user.email),
      })),
    [usersData]
  )

  function handleUserSelect(email: string) {
    if (email && !form.recipients.includes(email)) {
      updateForm({ recipients: [...form.recipients, email] })
    } else {
      addToast('warning', translations.toasts.emailAlreadySelected)
    }
  }

  function handleChangeEmail(value: string) {
    if (!value) {
      setEmailError(null)
      setEmail('')
      return
    }
    const validEmail = emailValidation(value)
    setEmailError(validEmail ? null : translations.errorEmail)
    setEmail(value)
  }

  function handleAddEmail() {
    const addEmail = email && !emailError && !form.recipients.includes(email)

    if (email && form.recipients.includes(email)) {
      addToast('warning', translations.toasts.emailAlreadySelected)
    }

    if (addEmail) {
      updateForm({ recipients: [...form.recipients, email] })
      setEmail('')
    }
  }

  function handleDeleteEmail(email: string) {
    const emails = form.recipients.filter(value => value !== email)
    updateForm({ recipients: [...emails] })
  }

  function handleClear() {
    setEmail('')
    setEmailError(null)
  }

  const iconLeftProps: ExtendedIconProps | undefined =
    email !== ''
      ? {
          icon: 'times',
          hoverColor: 'error',
          cursor: 'pointer',
          onClick: handleClear,
        }
      : undefined

  const iconRightProps: ExtendedIconProps | undefined =
    email !== '' && emailError === null
      ? {
          icon: 'plus-circle',
          hoverColor: 'primary',
          cursor: 'pointer',
          onClick: handleAddEmail,
        }
      : undefined

  return (
    <RegularModal
      title={translations.exportIssue}
      submitText={translations.export}
      width="400px"
      onCancel={() => onSubmit(null)}
      onSubmit={submit(onSubmit)}
    >
      <Form>
        <Field>
          <label>{translations.exportOption}</label>
          <Combobox<string>
            mode="single"
            value={form.exportOption}
            items={exportOptions}
            noSearch
            onChange={value =>
              updateForm({
                exportOption: (value as ExportOption) || ExportOption.DOWNLOAD,
              })
            }
          />
        </Field>
        {form.exportOption === ExportOption.DOWNLOAD && (
          <Field>
            <label>{translations.exportFormat}</label>
            <Combobox<string>
              mode="single"
              value={form.exportFormat}
              items={exportFormatOptions}
              noSearch
              onChange={value =>
                updateForm({
                  exportFormat: (value as ExportFormat) || ExportFormat.PDF,
                })
              }
            />
          </Field>
        )}
        {form.exportOption === ExportOption.MAIL && (
          <>
            <Field>
              <label>{translations.selectFromList}</label>
              <Combobox<string>
                mode="single"
                value=""
                items={userItems}
                onChange={handleUserSelect}
              />
              {errors.recipients && (
                <p className="mt-1 text-xs text-rose-500">
                  {errors.recipients}
                </p>
              )}
            </Field>
            <Field>
              <label>{translations.addWithEmail}</label>

              <Input
                value={email}
                error={emailError}
                fullWidth
                autoComplete="off"
                iconLeftProps={iconLeftProps}
                iconRightProps={iconRightProps}
                onChange={handleChangeEmail}
                onEnter={handleAddEmail}
              />
            </Field>

            {form.recipients.length > 0 && (
              <Field>
                <label>{translations.selectedRecipients}</label>
                <Users>
                  {form.recipients.map(email => (
                    <div className="userItem" key={email}>
                      <div className="text">{email}</div>
                      <Icon
                        className="close"
                        icon="times"
                        type="light"
                        size="1.2rem"
                        color="gray6"
                        hoverColor="secondary"
                        cursor="pointer"
                        onClick={() => handleDeleteEmail(email)}
                      />
                    </div>
                  ))}
                </Users>
              </Field>
            )}
            <Field>
              <label>{translations.customText}</label>
              <TextArea
                value={form.customExportText ?? ''}
                fullWidth
                rows={4}
                onChange={value => updateForm({ customExportText: value })}
              />
            </Field>
          </>
        )}
      </Form>
    </RegularModal>
  )
}
